import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import {
  collectLookupIdsFromConditions,
  filterMapToConditions,
  FilterOption,
  reconstructAndHydrateUiFilters,
  SelectedFilterMap,
  transformConditionsToFlatRecord,
} from 'src/app/utils/filter-utils';
import { CONDITIONS_TYPES, CUSTOM_FIELD_TYPES, Customer, SimpleConditionsWrapper } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomFieldsService } from 'src/services/custom-fields.service';
import { CustomersService } from 'src/services/customers.service';
import { JsmService, type JsmCredential, type JsmServiceDesk } from 'src/services/jsm.service';
import { MessageService } from 'src/services/message.service';
import { RelayRulesService, type RelayRule as RelayRuleBase } from 'src/services/relay-rules.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

interface RelayRule extends Partial<RelayRuleBase> {
  channel?: Customer;
  serviceDesk?: JsmServiceDesk;
  filters?: SelectedFilterMap; // This is only used in the UI to save the filter options - these are converted to Conditions before saving
}

interface RequestType {
  id: string;
  name: string;
}

interface RequestTypeDisplay {
  id: string | null;
  name: string | null;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxTippyModule, FilterButtonComponent, ChannelSelectorModule],
  selector: 'app-relay-rules',
  templateUrl: './relay-rules.component.html',
  styleUrls: ['./relay-rules.component.scss'],
})
export class RelayRulesComponent implements OnInit {
  @Input() projectId: string | null = null;
  @ViewChildren('filterButton') filterButtons: QueryList<FilterButtonComponent>;

  relayRules: RelayRule[] = [];
  hasRelayRules = false;
  isLoading = false;
  isInitializing = true;
  editingStates = new Map<number, RelayRule>();
  emptyFiltersMap = new Map();
  jsmCredentials: JsmCredential[] = [];
  requestTypesByChannel = new Map<string, RequestType[]>(); // channelId -> request types
  requestTypeDisplayByRule = new Map<number, RequestTypeDisplay>(); // ruleIndex -> display info

  REQUEST_TYPE_NONE_OPTION_LABEL = 'None - will not sync if no matching type found by name';

  // Filter options for conversation-level filtering (copied from edit-automation)
  filterOptions: FilterOption[] = [
    {
      label: 'Project',
      id: 'projectId',
      icon: 'custom-project',
      type: 'select',
    },
    {
      label: 'Assignee',
      id: 'assigneeId',
      searchId: 'assignedToUserId',
      icon: 'person',
      type: 'select',
    },
    !this.authService.tenant.accountsEnabled
      ? {
          label: 'Customer',
          id: 'customerId',
          icon: 'business',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Channel',
          id: 'customerId',
          icon: 'chatbubbles',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account',
          id: 'accountId',
          icon: 'business',
          type: 'select',
        }
      : undefined,
    {
      label: 'Ticket Type',
      id: 'ticketTypeId',
      icon: 'ticket',
      type: 'select',
    },
    {
      label: 'Custom Field',
      id: 'customFieldId',
      type: 'variable',
      icon: 'flask',
      searchablePopoverOptions: {
        allowedTypes: [
          CUSTOM_FIELD_TYPES.CHECKBOX,
          CUSTOM_FIELD_TYPES.SINGLE_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_SELECT,
          CUSTOM_FIELD_TYPES.DATE,
          CUSTOM_FIELD_TYPES.USER_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_USER_SELECT,
          CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT,
        ],
      },
    },
    {
      label: 'Tag',
      id: 'tagId',
      searchId: 'tags.id',
      icon: 'pricetag',
      type: 'select',
    },
    !this.authService.tenant.accountsEnabled
      ? {
          label: 'Customer Tag',
          id: 'customerTagId',
          searchId: 'customer.tags.id',
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Channel Tag',
          id: 'customerTagId',
          searchId: 'customer.tags.id',
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account Tag',
          id: 'accountTagId',
          searchId: 'account.tags.id',
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
    {
      label: 'Source',
      id: 'sourceType',
      icon: 'log-in',
      type: 'select',
      values: [
        {
          label: 'Slack',
          id: 'slack',
        },
        {
          label: 'Email',
          id: 'email',
        },
        {
          label: 'In-app',
          id: 'widget',
        },
        {
          label: 'Dashboard',
          id: 'dashboard',
        },
        {
          label: 'Microsoft Teams',
          id: 'microsoft-teams',
        },
        {
          label: 'Automation',
          id: 'automation',
        },
      ],
    },
    {
      label: 'Status Type',
      id: 'status',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: 'Status',
      id: 'subStatus.key',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: 'Priority',
      id: 'priority',
      icon: 'alert-circle',
      type: 'select',
    },
    {
      label: 'Is New?',
      id: 'isNew',
      icon: 'pulse',
      type: 'select',
      values: [
        {
          label: 'Yes',
          id: true,
        },
        {
          label: 'No',
          id: false,
        },
      ],
    },
    {
      label: 'Is During Working Hours?',
      id: 'isWorkingTime',
      icon: 'time',
      type: 'select',
      values: [
        {
          label: 'Yes',
          id: true,
        },
        {
          label: 'No',
          id: false,
        },
      ],
    },
    {
      label: 'Sentiment',
      id: 'sentimentNormalized',
      icon: 'happy',
      type: 'select',
      values: [
        {
          label: 'Very Positive',
          id: 'veryPositive',
        },
        {
          label: 'Positive',
          id: 'positive',
        },
        {
          label: 'Neutral',
          id: 'neutral',
        },
        {
          label: 'Negative',
          id: 'negative',
        },
        {
          label: 'Very Negative',
          id: 'veryNegative',
        },
      ],
    },
    {
      label: 'Is Autoresponse',
      id: 'isAutoresponse',
      icon: 'chatbox-ellipses',
      type: 'select',
      values: [
        {
          label: 'Yes',
          id: true,
        },
        {
          label: 'No',
          id: false,
        },
      ],
    },
  ].filter(Boolean) as FilterOption[];

  constructor(
    public authService: AuthService,
    private msgService: MessageService,
    private alertCtrl: AlertController,
    private apiService: ApiService,
    private customFieldsService: CustomFieldsService,
    private searchablePopoverService: SearchablePopoverService,
    private jsmService: JsmService,
    private customersService: CustomersService,
    private relayRulesService: RelayRulesService,
  ) {}

  async ngOnInit() {
    try {
      this.jsmCredentials = await this.jsmService.getConnectedCredentials();
    } catch (error: any) {
      this.msgService.showError(error || 'Failed to load JSM credentials');
    }

    await this.loadRelayRules();

    this.isInitializing = false;
  }

  async loadRelayRules() {
    try {
      this.isLoading = true;
      const listResponse = await this.relayRulesService.list({
        where: [
          {
            field: 'projectId',
            operator: '==',
            value: this.projectId ?? this.authService.selectedProject?.id ?? null,
          },
        ],
      });
      const response: any[] = listResponse.data;

      // Collect lookup IDs from all rules
      const lookupIds: Record<string, string[]> = {};
      for (const rule of response) {
        if (rule.conditions) {
          collectLookupIdsFromConditions(rule.conditions as SimpleConditionsWrapper, lookupIds);
        }
      }

      // Fetch lookup values from API
      let lookups: Record<string, Record<string, string>> = {};
      if (Object.keys(lookupIds).length > 0) {
        lookups = await this.apiService.postPromise('/saved-filters/get-lookup-values', lookupIds);

        // Add static lookups
        lookups = {
          ...lookups,
          isNew: { true: 'Yes', false: 'No' },
          isWorkingTime: { true: 'Yes', false: 'No' },
          isAutoresponse: { true: 'Yes', false: 'No' },
        };
      }

      // Transform and hydrate filters for each rule
      this.relayRules = await Promise.all(
        response.map(async (rule) => {
          // Transform conditions to flat format
          const flatFilters = transformConditionsToFlatRecord(
            rule.conditions?.type === CONDITIONS_TYPES.SIMPLE ? rule.conditions : undefined,
          );

          // Hydrate filters using the helper
          const filters = await reconstructAndHydrateUiFilters(
            flatFilters,
            lookups,
            this.filterOptions,
            this.apiService,
            this.customFieldsService,
          );

          return {
            ...rule,
            filters,
            channel: rule.channel || (rule.channelId ? { id: rule.channelId } : undefined),
          };
        }),
      );

      this.hasRelayRules = this.relayRules.length > 0;

      // Store initial editing states and populate request type display info
      for (const [index, rule] of this.relayRules.entries()) {
        this.editingStates.set(index, JSON.parse(JSON.stringify(rule)));

        const defaultRequestTypeId = rule.channel?.jsmChannel?.defaultJsmRequestTypeId;

        if (defaultRequestTypeId && rule.channel) {
          // Only load request types if we have a default set
          await this.loadRequestTypesForChannel(rule.channel);

          const requestTypes = this.requestTypesByChannel.get(rule.channelId!) || [];
          const requestType = requestTypes.find((rt) => rt.id === defaultRequestTypeId);

          this.requestTypeDisplayByRule.set(index, {
            id: defaultRequestTypeId,
            name: requestType?.name || null,
          });
        } else {
          this.requestTypeDisplayByRule.set(index, { id: null, name: null });
        }
      }

      // Only add empty rule if no rules exist
      if (this.relayRules.length === 0) {
        this.addRelayRule();
      }
    } catch (error: any) {
      this.msgService.showError(error || 'Failed to load relay rules');
      // Show empty state on error
      this.relayRules = [];
      this.hasRelayRules = false;
      this.addRelayRule();
    } finally {
      this.isLoading = false;
    }
  }

  addRelayRule() {
    const newRule: RelayRule = {
      channelId: null,
      conditions: {},
      filters: new Map(),
      enabled: true,
    };
    const newIndex = this.relayRules.length;

    this.relayRules.push(newRule);
    // Store original state for cancel functionality
    this.editingStates.set(newIndex, JSON.parse(JSON.stringify(newRule)));
    this.requestTypeDisplayByRule.set(newIndex, { id: null, name: null });

    if (this.relayRules.length > 1) {
      this.hasRelayRules = true;
    }
  }

  filtersDidChange(filters: SelectedFilterMap, index: number) {
    this.relayRules[index].filters = filters;
  }

  async showChannelSelector(index: number) {
    if (this.jsmCredentials.length === 0) {
      this.msgService.show('No JSM instances connected. Please connect a JSM instance first.');
      return;
    }

    // If only one credential, skip the first popover and go straight to service desks
    if (this.jsmCredentials.length === 1) {
      this.showServiceDeskSelector(index, this.jsmCredentials[0]);
      return;
    }

    // Multiple credentials - show credential selector first
    this.searchablePopoverService.jsmCredentials({
      event: event,
      callback: (_id: string, _name: string, credential: JsmCredential) => {
        this.showServiceDeskSelector(index, credential);
      },
    });
  }

  private async showServiceDeskSelector(index: number, credential: JsmCredential) {
    this.searchablePopoverService.jsmServiceDesks({
      event: event,
      jsmInstanceId: credential.jsmInstanceId,
      callback: async (serviceDeskId: string, _name: string, serviceDesk: JsmServiceDesk) => {
        // show the selected service desk name in the ui
        this.relayRules[index].serviceDesk = serviceDesk;
        await this.handleServiceDeskSelection(index, credential, serviceDesk);
      },
    });
  }

  private async handleServiceDeskSelection(index: number, credential: JsmCredential, serviceDesk: JsmServiceDesk) {
    try {
      this.isLoading = true;

      // Check if customer already exists for this service desk by querying jsmChannel
      const existingCustomers = await this.customersService.list({
        where: [
          {
            field: 'jsmChannel.jsmServiceDeskId',
            operator: '==',
            value: serviceDesk.id,
          },
          {
            field: 'projectId',
            operator: '==',
            value: this.projectId ?? this.authService.selectedProject?.id ?? null,
          },
        ],
        include: ['jsmChannel'],
        limit: 1,
      });

      let customer;

      if (existingCustomers.data.length > 0) {
        // Customer already exists, use it
        customer = existingCustomers.data[0];
      } else {
        // Customer doesn't exist, create it
        const result = await this.jsmService.bulkCreateChannels(
          this.projectId ?? this.authService.selectedProject?.id ?? '',
          credential.id,
          credential.jsmInstanceId,
          [
            {
              id: serviceDesk.id,
              name: serviceDesk.projectName,
            },
          ],
        );

        if (result.failed > 0 || result.results.length === 0) {
          throw new Error(result.errors?.[0]?.message || 'Failed to create JSM channel');
        }

        // Fetch the newly created customer
        customer = await this.customersService.get(result.results[0].channelId);
      }

      // Update the relay rule with the customer info
      this.relayRules[index].channel = customer;
      this.relayRules[index].channelId = customer.id;
    } catch (error: any) {
      this.msgService.showError(error || 'Failed to select JSM service desk');
    } finally {
      this.isLoading = false;
    }
  }

  private async loadRequestTypesForChannel(channel: Customer) {
    if (!channel.jsmChannel?.jsmInstanceId || !channel.jsmChannel?.jsmServiceDeskId) {
      return;
    }

    // Skip if already loaded to avoid duplicate API calls
    if (this.requestTypesByChannel.has(channel.id)) {
      return;
    }

    try {
      const response: RequestType[] = await this.jsmService.getAllRequestTypes(
        channel.jsmChannel.jsmInstanceId,
        channel.jsmChannel.jsmServiceDeskId,
      );

      this.requestTypesByChannel.set(channel.id, response ?? []);
    } catch (error: any) {
      console.error('Failed to load request types:', error);
      this.requestTypesByChannel.set(channel.id, []);
    }
  }

  async showRequestTypeSelector(index: number, event: Event) {
    const rule = this.relayRules[index];

    if (!rule.channelId || !rule.channel) {
      this.msgService.show('Please select a JSM project first');
      return;
    }

    const jsmInstanceId = rule.channel.jsmChannel?.jsmInstanceId;
    const jsmServiceDeskId = rule.channel.jsmChannel?.jsmServiceDeskId;

    if (!jsmInstanceId || !jsmServiceDeskId) {
      this.msgService.show('JSM channel is not properly configured');
      return;
    }

    const currentRequestTypeId = this.requestTypeDisplayByRule.get(index)?.id || undefined;

    this.searchablePopoverService.jsmRequestTypes({
      event,
      jsmInstanceId,
      jsmServiceDeskId,
      selectedRequestId: currentRequestTypeId,
      noneOptionLabel: this.REQUEST_TYPE_NONE_OPTION_LABEL,
      callback: (id: string, name: string) => {
        this.requestTypeDisplayByRule.set(index, { id, name });
      },
    });
  }

  async saveRelayRule(index: number) {
    const rule = this.relayRules[index];
    if (!rule) {
      return;
    }

    // Validation: channel must be specified
    if (!rule.channelId) {
      this.msgService.showError('Please select a channel before saving');
      return;
    }

    try {
      this.isLoading = true;

      const conditions = filterMapToConditions(rule.filters || new Map());

      // Update the channel's defaultRequestTypeId if it has changed
      const requestTypeDisplay = this.requestTypeDisplayByRule.get(index);
      const currentDefaultRequestTypeId = rule.channel?.jsmChannel?.defaultJsmRequestTypeId;

      if (requestTypeDisplay && requestTypeDisplay.id !== currentDefaultRequestTypeId) {
        await this.apiService.patchPromise(`/jsm-channels/${rule.channelId}/default-request-type`, {
          defaultJsmRequestTypeId: requestTypeDisplay.id,
        });
      }

      // Build request payload based on create vs update
      let payload: any;

      if (rule.id) {
        // UPDATE: Only send fields allowed by updateRelayRuleSchema
        // Schema: enabled, conditions, includePrivateMessages (all optional)
        payload = {
          id: rule.id,
          enabled: rule.enabled ?? false,
          conditions: conditions,
        };
      } else {
        // CREATE: Send fields required by createRelayRuleSchema
        // Schema: channelId (required), enabled, conditions, includePrivateMessages (all optional)
        // Also need projectId
        payload = {
          channelId: rule.channelId,
          enabled: rule.enabled ?? false,
          conditions: conditions,
          projectId: this.projectId ?? this.authService.selectedProject?.id ?? null,
        };
      }

      const response = await this.relayRulesService.save(payload);

      // Update the rule with the response data and the updated channel defaultRequestTypeId
      this.relayRules[index] = {
        ...rule,
        ...(response || {}),
        id: response?.id,
        conditions: conditions, // Store the converted conditions
        channel: {
          ...rule.channel,
          jsmChannel: {
            ...rule.channel?.jsmChannel,
            defaultJsmRequestTypeId: requestTypeDisplay?.id || null,
          },
        },
      };

      // Update editing state
      this.editingStates.set(index, JSON.parse(JSON.stringify(this.relayRules[index])));
      this.hasRelayRules = true;
      this.msgService.show('Relay rule saved successfully');
    } catch (error: any) {
      this.msgService.showError(error || 'Failed to save relay rule');
    } finally {
      this.isLoading = false;
    }
  }

  cancelEdit(index: number) {
    const rule = this.relayRules[index];

    // If it's a new rule (no id), just remove it
    if (!rule.id) {
      this.relayRules.splice(index, 1);
      this.editingStates.delete(index);
      this.requestTypeDisplayByRule.delete(index);
      if (this.relayRules.length === 0) {
        this.hasRelayRules = false;
        this.addRelayRule();
      }
      return;
    }

    // Revert to saved state
    const savedState = this.editingStates.get(index);
    if (savedState) {
      this.relayRules[index] = JSON.parse(JSON.stringify(savedState));

      // Restore request type display info
      const defaultRequestTypeId = savedState.channel?.jsmChannel?.defaultJsmRequestTypeId;
      if (defaultRequestTypeId && savedState.channelId) {
        const requestTypes = this.requestTypesByChannel.get(savedState.channelId) || [];
        const requestType = requestTypes.find((rt) => rt.id === defaultRequestTypeId);
        this.requestTypeDisplayByRule.set(index, {
          id: defaultRequestTypeId,
          name: requestType?.name || null,
        });
      } else {
        this.requestTypeDisplayByRule.set(index, { id: null, name: null });
      }
    }
  }

  async deleteRelayRule(index: number) {
    const rule = this.relayRules[index];
    if (!rule) {
      return;
    }

    // If it's a new rule (no id), just remove it
    if (!rule.id) {
      this.relayRules.splice(index, 1);
      this.editingStates.delete(index);
      this.requestTypeDisplayByRule.delete(index);
      if (this.relayRules.length === 0) {
        this.hasRelayRules = false;
        this.addRelayRule();
      }
      return;
    }

    // For existing rules, show confirmation
    const alert = await this.alertCtrl.create({
      header: 'Delete Relay Rule',
      message: 'Are you sure you want to delete this relay rule?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            try {
              this.isLoading = true;
              // Call backend DELETE endpoint
              await this.relayRulesService.delete(rule.id!);

              // Remove from UI
              this.relayRules.splice(index, 1);
              this.editingStates.delete(index);
              this.requestTypeDisplayByRule.delete(index);
              if (this.relayRules.length === 0) {
                this.hasRelayRules = false;
                this.addRelayRule();
              }
              this.msgService.show('Relay rule deleted');
            } catch (error: any) {
              this.msgService.showError(error || 'Failed to delete relay rule');
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
    });

    await alert.present();
  }

  addFilter(event: Event, index: number) {
    const filterButton = this.filterButtons.toArray()[index];
    if (filterButton) {
      filterButton.addFilter(event);
    }
  }
}
